<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs équipements"
      subtitle="Statistiques et données sur les équipements des véhicules"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/vehicules" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-4 mb-4">
        <div class="card h-100 border-primary">
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des équipements</h5>
            <div class="display-4">{{ equipements.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4 mb-4">
        <div class="card h-100 border-success">
          <div class="card-body text-center">
            <h5 class="card-title text-success">Véhicules équipés</h5>
            <div class="display-4">{{ vehiculesEquipes.length }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-4 mb-4">
        <div class="card h-100 border-info">
          <div class="card-body text-center">
            <h5 class="card-title text-info">
              Équipements moyens par véhicule
            </h5>
            <div class="display-4">
              {{ equipementsMoyensParVehicule.toFixed(1) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique de répartition par type d'équipement -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Répartition par type d'équipement</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la référence par le composant apexchart -->
              <apexchart
                type="pie"
                height="300"
                :options="typeChartOptions"
                :series="typeChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique d'évolution mensuelle des affectations -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-dark text-white">
            <h5 class="mb-0">Évolution mensuelle des affectations</h5>
          </div>
          <div class="card-body">
            <div
              class="chart-container"
              style="position: relative; height:300px;"
            >
              <!-- Remplacer la référence par le composant apexchart -->
              <apexchart
                type="bar"
                height="300"
                :options="monthlyChartOptions"
                :series="monthlySeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Répartition par type d'équipement -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-primary text-white">
            <h5 class="mb-0">Répartition par type d'équipement</h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-center">Nombre</th>
                  <th class="text-center">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(count, index) in equipementsParType" :key="index">
                  <td>{{ getTypeLabel(index) }}</td>
                  <td class="text-center">{{ count }}</td>
                  <td class="text-center">
                    {{ ((count / equipements.length) * 100).toFixed(1) }}%
                    <div class="progress mt-1" style="height: 5px;">
                      <div
                        class="progress-bar bg-primary"
                        :style="`width: ${(count / equipements.length) * 100}%`"
                      ></div>
                    </div>
                  </td>
                </tr>
                <tr v-if="Object.keys(equipementsParType).length === 0">
                  <td colspan="3" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top 10 des véhicules avec le plus d'équipements -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-dark">
            <h5 class="mb-0">
              Top 10 des véhicules avec le plus d'équipements
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Véhicule</th>
                  <th>Immatriculation</th>
                  <th class="text-center">Nb équipements</th>
                  <th class="text-center">Détails</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicule in topVehiculesEquipements"
                  :key="vehicule.vehicule_id"
                >
                  <td>{{ vehicule.modele || "Non défini" }}</td>
                  <td>{{ vehicule.immatriculation || "Non défini" }}</td>
                  <td class="text-center">
                    <span class="badge badge-warning">
                      {{ vehicule.nbEquipements }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${vehicule.vehicule_id}?tab=equipements`
                      "
                      class="btn btn-sm btn-outline-info"
                    >
                      Détails
                    </router-link>
                  </td>
                </tr>
                <tr v-if="topVehiculesEquipements.length === 0">
                  <td colspan="4" class="text-center">
                    Aucune donnée disponible
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Liste des derniers équipements ajoutés -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-secondary text-white">
            <h5 class="mb-0">
              10 derniers équipements ajoutés
            </h5>
          </div>
          <div class="card-body">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Véhicule</th>
                  <th>Type d'équipement</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="equipement in derniersEquipements"
                  :key="equipement.id"
                >
                  <td>{{ formatDate(equipement.date_affectation) }}</td>
                  <td>
                    <span class="badge badge-light">{{
                      equipement.immatriculation
                    }}</span>
                    {{ equipement.modele }}
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="getTypeBadgeClass(equipement.type)"
                    >
                      {{ getTypeLabel(equipement.type) }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="
                        `/vehicules/detail/${equipement.vehicule_id}?tab=equipements`
                      "
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="derniersEquipements.length === 0">
                  <td colspan="4" class="text-center">
                    Aucun équipement enregistré
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errHandler } from "@/helpers.js";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      equipements: [],
      loading: false,

      typesEquipement: [
        { id: 0, description: "Chaînes" },
        { id: 1, description: "Barres de toit" },
        { id: 2, description: "Autre" }
      ]
    };
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.monthlyChart) {
      this.monthlyChart.destroy();
    }
    if (this.typeChart) {
      this.typeChart.destroy();
    }
  },
  methods: {
    fetchData() {
      this.loading = true;

      this.$http
        .get("/vehicules-equipements")
        .then(response => {
          if (typeof response.data === "string") {
            console.warn(
              "Les données reçues sont au format texte, pas au format JSON"
            );
            this.equipements = [];
          } else {
            this.equipements = response.data;
          }

          this.$nextTick(() => {
            this.initCharts();
          });
        })
        .catch(error => {
          console.error(
            "Erreur lors du chargement des données:",
            errHandler(error)
          );
          this.$toast.error(
            "Impossible de charger les données des équipements"
          );
          this.equipements = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getMonthlyData() {
      const monthlyData = {};
      const now = moment();
      const sixMonthsAgo = moment().subtract(6, "months");

      // Initialiser les 6 derniers mois avec des compteurs à 0
      for (
        let m = moment(sixMonthsAgo);
        m.isSameOrBefore(now, "month");
        m.add(1, "month")
      ) {
        const monthKey = m.format("YYYY-MM");
        monthlyData[monthKey] = 0;
      }

      // Remplir les données réelles
      this.equipements.forEach(equipement => {
        if (!equipement.date_affectation) return;

        const monthKey = moment(equipement.date_affectation).format("YYYY-MM");
        if (monthlyData[monthKey] !== undefined) {
          monthlyData[monthKey]++;
        }
      });

      // Formater pour ApexCharts
      const labels = Object.keys(monthlyData).map(monthKey =>
        moment(monthKey, "YYYY-MM").format("MMM YYYY")
      );

      return {
        labels,
        counts: Object.values(monthlyData)
      };
    },
    getTypeData() {
      // Calculer les données par type d'équipement
      const typesData = {};
      const typesLabels = [];

      // Initialiser les compteurs
      this.typesEquipement.forEach(type => {
        typesData[type.id] = 0;
        typesLabels[type.id] = type.description;
      });

      // Compter les équipements par type
      this.equipements.forEach(equipement => {
        if (typesData[equipement.type] !== undefined) {
          typesData[equipement.type]++;
        } else {
          typesData[equipement.type] = 1;
        }
      });

      // Préparer les séries et labels pour le graphique
      const series = Object.values(typesData);
      const labels = Object.keys(typesData).map(
        key => typesLabels[key] || `Type ${key}`
      );

      return { series, labels };
    },
    exportStats() {
      this.loading = true;

      this.$http
        .get("/vehicules-equipements-export", {
          responseType: "blob"
        })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          const contentDisposition = response.headers["content-disposition"];
          let filename = "equipements-export.xls";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }

          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast.success("Export téléchargé avec succès");
        })
        .catch(error => {
          console.error("Erreur lors de l'export:", errHandler(error));
          this.$toast.error("Impossible d'exporter les données");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getTypeLabel(typeId) {
      const type = this.typesEquipement.find(t => t.id === parseInt(typeId));
      return type ? type.description : "Non défini";
    },

    getTypeBadgeClass(typeId) {
      typeId = parseInt(typeId);
      switch (typeId) {
        case 0:
          return "badge-primary"; // Chaînes
        case 1:
          return "badge-success"; // Barres de toit
        case 2:
          return "badge-info"; // Autre
        default:
          return "badge-secondary";
      }
    },

    formatDate(date) {
      if (!date) return "Non défini";
      return moment(date).format("DD/MM/YYYY");
    }
  },
  computed: {
    monthlySeries() {
      const monthlyData = this.getMonthlyData();
      return [
        {
          name: "Nombre d'équipements",
          data: monthlyData.counts
        }
      ];
    },
    monthlyChartOptions() {
      const monthlyData = this.getMonthlyData();
      return {
        chart: {
          type: "bar",
          height: 300,
          toolbar: {
            show: true
          },
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800
          },
          background: "transparent"
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
            distributed: false
          }
        },
        colors: ["#36A2EB"],
        fill: {
          opacity: 0.85
        },
        labels: monthlyData.labels,
        xaxis: {
          type: "category",
          labels: {
            rotateAlways: false
          }
        },
        yaxis: {
          title: {
            text: "Nombre d'équipements"
          },
          labels: {
            formatter: function(val) {
              return val.toFixed(0);
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val;
          },
          offsetY: -20,
          style: {
            fontSize: "12px",
            colors: ["#304758"]
          }
        },
        tooltip: {
          y: {
            formatter: function(value) {
              return value + " équipement(s)";
            }
          }
        },
        legend: {
          position: "top"
        },
        grid: {
          borderColor: "#e0e0e0",
          strokeDashArray: 5
        },
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },
    typeChartSeries() {
      const typeData = this.getTypeData();
      return typeData.series;
    },

    typeChartOptions() {
      const typeData = this.getTypeData();
      return {
        chart: {
          type: "pie",
          height: 300,
          background: "transparent"
        },
        labels: typeData.labels,
        colors: ["#FF6384", "#36A2EB", "#4BC0C0", "#FFCE56", "#FF9F40"],
        legend: {
          position: "bottom"
        },
        dataLabels: {
          formatter: function(val) {
            return val.toFixed(1) + "%";
          }
        },
        tooltip: {
          y: {
            formatter: function(val, { w }) {
              const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              const percent = ((val / total) * 100).toFixed(1);
              return `${val} (${percent}%)`;
            }
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: "bottom"
              }
            }
          }
        ],
        theme: {
          mode:
            this.$store && this.$store.getters["colors/darkMode"]
              ? "dark"
              : "light"
        }
      };
    },
    vehiculesEquipes() {
      // Récupérer les IDs uniques des véhicules qui ont des équipements
      const vehiculeIdsSet = new Set();
      this.equipements.forEach(equipement => {
        if (equipement.vehicule_id) {
          vehiculeIdsSet.add(equipement.vehicule_id);
        }
      });

      // Convertir en tableau d'objets
      return Array.from(vehiculeIdsSet).map(id => ({ id }));
    },

    equipementsMoyensParVehicule() {
      if (this.vehiculesEquipes.length === 0) return 0;
      return this.equipements.length / this.vehiculesEquipes.length;
    },

    equipementsParType() {
      const typeCount = {};

      this.typesEquipement.forEach(type => {
        typeCount[type.id] = 0;
      });

      this.equipements.forEach(equipement => {
        const typeId = parseInt(equipement.type);
        if (typeCount[typeId] !== undefined) {
          typeCount[typeId]++;
        } else {
          typeCount[typeId] = 1;
        }
      });

      return typeCount;
    },

    topVehiculesEquipements() {
      // Regrouper par véhicule
      const vehiculesMap = {};

      this.equipements.forEach(equipement => {
        if (!equipement.vehicule_id) return;

        if (!vehiculesMap[equipement.vehicule_id]) {
          vehiculesMap[equipement.vehicule_id] = {
            vehicule_id: equipement.vehicule_id,
            immatriculation: equipement.immatriculation || "Non défini",
            modele: equipement.modele || "Non défini",
            nbEquipements: 0
          };
        }

        vehiculesMap[equipement.vehicule_id].nbEquipements++;
      });

      // Convertir en tableau et trier
      return Object.values(vehiculesMap)
        .sort((a, b) => b.nbEquipements - a.nbEquipements)
        .slice(0, 10);
    },

    derniersEquipements() {
      // Filtrer les équipements qui ont une date
      const equipementsAvecDate = this.equipements.filter(
        e => e.date_affectation
      );

      // Trier par date décroissante (plus récent d'abord)
      return [...equipementsAvecDate]
        .sort((a, b) => {
          const dateA = new Date(a.date_affectation);
          const dateB = new Date(b.date_affectation);
          return dateB - dateA;
        })
        .slice(0, 10); // Prendre les 10 premiers résultats
    }
  }
};
</script>

<style lang="css">
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  font-weight: 500;
}

.table th {
  background-color: #f8f9fa;
}

.progress {
  border-radius: 0.5rem;
  overflow: hidden;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.75em;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-primary {
  background-color: #007bff;
  color: white;
}

.border-primary {
  border-top: 4px solid #007bff !important;
}

.border-info {
  border-top: 4px solid #17a2b8 !important;
}

.border-success {
  border-top: 4px solid #28a745 !important;
}

.border-warning {
  border-top: 4px solid #ffc107 !important;
}

.border-danger {
  border-top: 4px solid #dc3545 !important;
}

.chart-container {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
}
</style>
